import { useMutation, useQueryClient } from '@tanstack/react-query'; //-tech-debt-react-query;
import { deleteItem, postItem, putItem } from '../support/queries';
import { CacheKey } from '../types/cache-keys';
import { GtmContentSpec } from '../components/Admin/GtmContent/gtmContentSpecs';

/**
 * Writes for the architecture page content tables.
 *
 * Reads deliberately have no hooks of their own: every one of these collections already travels
 * inside the detailed `db/go-to-market/<id>` payload the editor loads, so the panels read rows
 * from there. A write only has to land and then invalidate that payload — which also refreshes
 * the portal page, since it queries the same record.
 */

/** The prefix every go-to-market detail and list query shares. */
const GTM_QUERY_PREFIX = [String(CacheKey.GtmSolution), String(CacheKey.GtmArchitecture)];

const useGtmContentMutations = (spec: GtmContentSpec) => {
  const queryClient = useQueryClient();

  const onSettled = () => {
    queryClient.invalidateQueries({ queryKey: GTM_QUERY_PREFIX });
    queryClient.invalidateQueries({ queryKey: [String(spec.cacheKey)] });
  };

  const create = useMutation({
    mutationFn: (payload: Record<string, any>) => postItem(spec.endpoint, payload),
    onSettled,
  });

  const update = useMutation({
    mutationFn: (payload: Record<string, any> & { id: number }) => putItem(spec.endpoint, payload),
    onSettled,
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteItem(`${spec.endpoint}/${id}`),
    onSettled,
  });

  return {
    create,
    update,
    remove,
    isSaving: create.isPending || update.isPending || remove.isPending,
    error: create.error || update.error || remove.error,
  };
};

export { GTM_QUERY_PREFIX, useGtmContentMutations };
